import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Building2, User, Mail, Plus, Check } from "lucide-react";
import AddClient from "../Portefeuille/Create.js";
import FormLead from "../Lead/FormLead.js";

import { useEtatGlobal } from '../EtatGlobal';
import { useClients } from '../Portefeuille/useClients.js';

export default function ClientSearch({
  setFormData,
  setSelectedClientId,
  setSelectedLeadId,
  setItemType,
  initialClientId,
}) {
  const [searchClient, setSearchClient] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showAddClientOverlay, setShowAddClientOverlay] = React.useState(false);
  const [showFromLeadOverlay, setShowFromLeadOverlay] = React.useState(false);

  const { leads, annuaireData, setClientIdGlobal } = useEtatGlobal();
  const { data: clients = [] } = useClients();

  const overlayRef = useRef(null);
  const inoverlayRef = useRef(null);

  useEffect(() => {
    if (isLocked) return;

    const q = searchClient;

    if (q.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    const handler = setTimeout(() => {
      setIsSearching(true);
      try {
        const terms = q.toLowerCase().split(" ").filter(Boolean);

        // Clients — filtre en mémoire (préchargé au démarrage)
        const clientData = clients
          .filter((c) => terms.every((term) => (c.nom || "").toLowerCase().includes(term)))
          .slice(0, 25);

        // Leads — filtre en mémoire (préchargé au démarrage)
        const filteredLeads = leads.filter((l) => {
          const fullName = `${l.nom || ""} ${l.prenom || ""}`.toLowerCase();
          return terms.every((term) => fullName.includes(term));
        });

        // Annuaire — filtre en mémoire, proposé pour tous les types de LM
        // (pas seulement "LM IR.docx" comme avant) : les personnes du
        // répertoire sont désormais une source de sélection à part entière,
        // combinée aux clients/prospects plutôt que de les remplacer.
        const sq = q.toLowerCase();
        const filteredAnnuaire = annuaireData
          .filter((a) => {
            const fullName = `${a.nom || ""} ${a.prenoms || ""}`.toLowerCase();
            return (
              terms.every((term) => fullName.includes(term)) ||
              (a.email || "").toLowerCase().includes(sq)
            );
          })
          .slice(0, 25)
          .map((a) => ({
            ...a,
            type: "annuaire",
            displayName: `${a.nom || ""} ${a.prenoms || ""}`.trim(),
          }));

        const combined = [
          ...clientData.map((c) => ({ ...c, type: "client" })),
          ...filteredLeads.map((l) => ({
            ...l,
            type: "prospect",
            displayName: `${l.nom || ""} ${l.prenom || ""}`.trim(),
          })),
          ...filteredAnnuaire,
        ];

        setSuggestions(combined);
        setIsSearching(false);
        setIsExpanded(true);
      } catch (e) {
        console.error("Erreur recherche :", e);
        setSuggestions([]);
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [searchClient, isLocked, clients, leads, annuaireData]);

  const handleSearchClient = (e) => {
    if (isLocked) return;
    setSearchClient(e.target.value);
  };

  const selectItem = async (item) => {
    setIsLocked(true);
    setSelectedItem(item);
    setSearchClient(
      item.type === "annuaire"
        ? item.displayName
        : item.nom || item.societeNom || item.displayName
    );

    // On vide ou filtre les suggestions pour ne pas afficher l’élément sélectionné
    setSuggestions((prev) => prev.filter((s) => s.id !== item.id));

    setIsExpanded(false);
    setItemType(item.type);

    if (item.type === "client") {
      setSelectedClientId(item.id);
      // Données déjà préchargées — pas de requête réseau
      const data = clients.find((c) => c.id === item.id) || item;
      const civiliteValue = data.civilite || "Monsieur";
      const FORMES_VALIDES = ["EI","EIRL","EARL","EURL","SARL","SASU","SAS","SCI","SCM","SCCV","LMNP","GFA","SC","SCP","SEL","SELARL"];
      const rawForme = Array.isArray(data.formeSociale) ? data.formeSociale[0] : data.formeSociale;
      const formeSociale = FORMES_VALIDES.includes(rawForme) ? rawForme : "EI";
      // Date de clôture : découpage par slice sur l'ISO (pas new Date(), qui
      // décalerait le jour selon le fuseau local) — même méthode que
      // Portefeuille/Update.js pour ce même champ.
      const dateClotureJour = data.dateCloture ? data.dateCloture.slice(8, 10) : "";
      const dateClotureMois = data.dateCloture ? data.dateCloture.slice(5, 7) : "";
      // Expert-comptable : repris du client si la valeur correspond
      // exactement à une des 2 options des selects "Expert-Comptable"/
      // "Signataire" des formulaires LM, sinon "Samuel HOARAU" par défaut
      // (même principe que formeSociale juste au-dessus) — un select ne peut
      // visuellement afficher qu'une option même quand aucune n'est
      // "sélectionnée" dans formData (il retombe sur la 1ère au rendu), donc
      // laisser la valeur vide ici créait un décalage entre ce qui
      // s'affichait (option 1 par défaut du navigateur) et la vraie valeur
      // enregistrée (vide, rejetée à la validation).
      const EXPERTS_COMPTABLES_VALIDES = ["Samuel HOARAU", "Anthony HOARAU"];
      const expertComptableValue = EXPERTS_COMPTABLES_VALIDES.includes(data.expertComptable)
        ? data.expertComptable
        : "Samuel HOARAU";
      setFormData((prev) => ({
        ...prev,
        nom_client: data.nom || " ",
        forme_sociale: formeSociale,
        civilite_lm: civiliteValue || "Monsieur",
        civilite_sign: civiliteValue === "Monsieur" ? "Le client" : "La cliente",
        nom: data.nomClient || " ",
        prenom: data.prenomClient || " ",
        email: data.email || " ",
        telephone: data.telephone || " ",
        // Issu de la fiche client (voir FicheClientOverlay.js) — évite de
        // ressaisir ces champs à la main pour un client déjà en base.
        adresse: data.adresse || " ",
        siren: data.siren || " ",
        siret: data.siret || " ",
        acticite_principale: data.activite || " ",
        date_cloture_jour: dateClotureJour || prev.date_cloture_jour,
        date_cloture_mois: dateClotureMois || prev.date_cloture_mois,
        expert_comptable_nom: expertComptableValue,
        expert_comptable: expertComptableValue,
      }));
      setSelectedItem({
        ...item,
        legal_form: formeSociale,
        contact_civility: data.civilite || "Monsieur",
        contact_first_name: data.prenomClient || "",
        contact_last_name: data.nomClient || "",
        contact_email: data.email || "",
      });
    } else if (item.type === "annuaire") {
      const clients = item.clients || [];
      const firstClient = clients[0];
      const adresseParts = [
        item.rue,
        [item.codePostal, item.villeAdresse].filter(Boolean).join(" "),
      ].filter(Boolean);
      const adresse = adresseParts.join(", ");
      setFormData((prev) => ({
        ...prev,
        nom: item.nom || " ",
        prenom: item.prenoms || " ",
        email: item.email || " ",
        telephone: item.telephone || " ",
        adresse: adresse || " ",
        nom_client: firstClient?.nom || " ",
        annuaireClients: clients,
      }));
    } else {
      // type prospect
      setSelectedLeadId(item.id);
      const civiliteValue = item.civilite === "M." ? "Monsieur" : "Madame";
      setFormData((prev) => ({
        ...prev,
        nom_client: item.societeNom || " ",
        nom: item.nom || " ",
        prenom: item.prenom || " ",
        civilite_lm: civiliteValue,
        civilite_sign:
          civiliteValue === "Monsieur" ? "Le client" : "La cliente",
        email: item.mail || " ",
        telephone: item.tel || " ",
        adresse: item.adresse || " ",
        siren: item.sirenSociete || " ",
        siret: item.sirenSociete || " ",
        forme_sociale: item.formeSociale || "EI",
      }));
    }
  };

  // Pré-sélection depuis une autre page (ex. "Création LM" sur la fiche
  // client) : réutilise selectItem tel quel pour garantir exactement le même
  // effet (formData, selectedClientId, itemType...) qu'une sélection manuelle
  // via la recherche. Ne se déclenche qu'une fois, dès que les clients
  // préchargés sont disponibles.
  //
  // clientIdGlobal est remis à null juste après consommation : ce n'est pas
  // seulement une protection locale (appliedInitialClientRef, qui repart à
  // false à chaque remount de ce composant) — sans ce reset, revenir sur
  // SignModif plus tard pour un AUTRE client (ex. après avoir visualisé et
  // cliqué "Revenir sur l'Application") réappliquait silencieusement ce
  // vieux client au remount suivant et écrasait le formData en cours de
  // saisie (bug remonté : "infos d'une ancienne LM, d'un autre client").
  const appliedInitialClientRef = useRef(false);
  useEffect(() => {
    if (
      !initialClientId ||
      appliedInitialClientRef.current ||
      clients.length === 0
    )
      return;
    appliedInitialClientRef.current = true;
    selectItem({ id: initialClientId, type: "client" });
    setClientIdGlobal(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialClientId, clients]);

  const showEmptyState =
    isFocused && searchClient.length >= 2 && suggestions.length === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* {isLoading && (
        <div
          style={{
            padding: "24px",
            textAlign: "center",
            color: "#64748b",
            fontStyle: "italic",
          }}
        >
          Chargement des données...
        </div>
      )} */}

      {selectedItem && !isExpanded ? (
        <div
          style={{
            border: "2px solid #8B1D41",
            background: "rgba(139,29,65,0.05)",
            borderRadius: "12px",
            padding: "16px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", gap: "12px", flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "8px",
                background: "#8B1D41",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Building2
                style={{ width: "24px", height: "24px", color: "white" }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  marginBottom: "4px",
                }}
              >
                <h4
                  style={{
                    fontWeight: 600,
                    color: "#1e293b",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {selectedItem.displayName ||
                    selectedItem.nom ||
                    selectedItem.societeNom}
                </h4>
                <span
                  style={{
                    fontSize: "10px",
                    padding: "2px 6px",
                    borderRadius: "6px",
                    background:
                      selectedItem.type === "client"
                        ? "#d1fae5"
                        : selectedItem.type === "annuaire"
                        ? "#dbeafe"
                        : "#fef08a",
                    color:
                      selectedItem.type === "client"
                        ? "#065f46"
                        : selectedItem.type === "annuaire"
                        ? "#1d4ed8"
                        : "#b91c1c",
                  }}
                >
                  {selectedItem.type === "client"
                    ? "Client"
                    : selectedItem.type === "annuaire"
                    ? "Annuaire"
                    : "Prospect"}
                </span>
              </div>

              {selectedItem.type === "client" && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    fontSize: "12px",
                    color: "#64748b",
                  }}
                >
                  <>
                    {selectedItem.contact_last_name && (
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <User style={{ width: "14px", height: "14px" }} />
                        {selectedItem.contact_civility}{" "}
                        {selectedItem.contact_first_name}{" "}
                        {selectedItem.contact_last_name}
                      </div>
                    )}
                    {selectedItem.contact_email && (
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Mail className="w-3.5 h-3.5" />
                        {selectedItem.contact_email}
                      </div>
                    )}
                  </>
                </div>
              )}

              {selectedItem.type === "prospect" && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    fontSize: "12px",
                    color: "#64748b",
                  }}
                >
                  {selectedItem.nom && (
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <User className="w-3.5 h-3.5" />
                      {selectedItem.nom} {selectedItem.prenom}
                    </div>
                  )}
                  {selectedItem.mail && (
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <Mail className="w-3.5 h-3.5" />
                      {selectedItem.mail}
                    </div>
                  )}
                </div>
              )}

              {selectedItem.type === "annuaire" && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    fontSize: "12px",
                    color: "#64748b",
                  }}
                >
                  {selectedItem.nom && (
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <User className="w-3.5 h-3.5" />
                      {selectedItem.nom} {selectedItem.prenoms}
                    </div>
                  )}
                  {selectedItem.email && (
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <Mail className="w-3.5 h-3.5" />
                      {selectedItem.email}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => {
              setIsLocked(false);
              setIsExpanded(true);
              setSearchClient("");
              setSelectedItem(null);
              setSelectedClientId("");
              setSelectedLeadId("");
              // Reset des champs issus du client/prospect/annuaire
              // précédent — sans ça, ils restaient affichés (et pouvaient
              // être soumis tels quels) tant qu'une nouvelle sélection ne
              // les écrasait pas explicitement.
              setFormData((prev) => ({
                ...prev,
                nom_client: " ",
                forme_sociale: "EI",
                civilite_lm: "Monsieur",
                civilite_sign: "Le client",
                nom: " ",
                prenom: " ",
                email: " ",
                telephone: " ",
                adresse: " ",
                siren: " ",
                siret: " ",
                acticite_principale: " ",
                date_cloture_jour: "",
                date_cloture_mois: "",
                // "Samuel HOARAU" (même logique que forme_sociale: "EI"
                // juste au-dessus) — jamais vide : un select ne peut pas
                // visuellement représenter "aucune valeur" parmi ses seules
                // options réelles, donc le laisser vide ici créait le même
                // décalage affichage/validation que lors du préremplissage
                // client (voir selectItem plus haut).
                expert_comptable_nom: "Samuel HOARAU",
                expert_comptable: "Samuel HOARAU",
                annuaireClients: [],
              }));
            }}
            style={{
              background: "transparent",
              border: "none",
              color: "#8B1D41",
              cursor: "pointer",
              padding: "4px 8px",
            }}
          >
            Modifier
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: "8px" }}>
            <div style={{ position: "relative", flex: 1 }}>
              <Search
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "20px",
                  height: "20px",
                  color: "#94a3b8",
                }}
              />
              <input
                type="text"
                placeholder="Rechercher par Nom ou Prénom ..."
                value={searchClient}
                onChange={handleSearchClient}
                onFocus={() => {
                  setIsFocused(true);
                  setIsExpanded(true);
                }}
                style={{
                  paddingLeft: "36px",
                  height: "48px",
                  width: "100%",
                  border: "2px solid #e2e8f0",
                  borderRadius: "12px",
                  outline: "none",
                }}
              />
            </div>
            <button
              onClick={() => setShowOverlay(true)}
              style={{
                height: "48px",
                padding: "0 16px",
                border: "2px dashed #cbd5e1",
                borderRadius: "12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              className="bg-white"
            >
              <Plus style={{ width: "20px", height: "20px" }} /> Nouveau
            </button>
          </div>

          <AnimatePresence>
            {isExpanded && (!selectedItem || searchClient.length > 0) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                style={
                  showEmptyState
                    ? {
                        border: "2px solid #e2e8f0",
                        borderRadius: "12px",
                        background: "white",
                        overflow: "hidden",
                        maxHeight: "300px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      }
                    : {}
                }
              >
                {showEmptyState ? (
                  <div
                    style={{
                      padding: "32px",
                      textAlign: "center",
                      color: "#94a3b8",
                    }}
                  >
                    <Building2
                      style={{
                        width: "48px",
                        height: "48px",
                        margin: "0 auto 12px",
                      }}
                    />
                    {isSearching ? "Recherche ... " : "Aucun résultat trouvé"}
                  </div>
                ) : (
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {suggestions.map((item) => (
                      <motion.div
                        key={`${item.type}-${item.id}`}
                        onClick={() => selectItem(item)}
                        style={{
                          padding: "12px",
                          cursor: "pointer",
                          display: "flex",
                          gap: "12px",
                          alignItems: "center",
                          backgroundColor:
                            selectedItem?.id === item.id
                              ? "rgba(139,29,65,0.05)"
                              : "transparent",
                        }}
                        whileHover={{ backgroundColor: "#f8fafc" }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor:
                              selectedItem?.id === item.id
                                ? "#8B1D41"
                                : "#f1f5f9",
                            color:
                              selectedItem?.id === item.id
                                ? "white"
                                : "#475569",
                          }}
                        >
                          {selectedItem?.id === item.id ? (
                            <Check style={{ width: "20px", height: "20px" }} />
                          ) : (
                            <Building2
                              style={{ width: "20px", height: "20px" }}
                            />
                          )}
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                              alignItems: "center",
                              marginBottom: "2px",
                            }}
                          >
                            <p
                              style={{
                                fontWeight: 600,
                                fontSize: "14px",
                                color: "#1e293b",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item.displayName || item.nom || item.societeNom}
                            </p>
                            <span
                              style={{
                                fontSize: "10px",
                                padding: "2px 6px",
                                borderRadius: "6px",
                                background:
                                  item.type === "client"
                                    ? "#d1fae5"
                                    : item.type === "annuaire"
                                    ? "#dbeafe"
                                    : "#fef08a",
                                color:
                                  item.type === "client"
                                    ? "#065f46"
                                    : item.type === "annuaire"
                                    ? "#1d4ed8"
                                    : "#b91c1c",
                              }}
                            >
                              {item.type === "client"
                                ? "Client"
                                : item.type === "annuaire"
                                ? "Annuaire"
                                : "Prospect"}
                            </span>
                          </div>
                          {/* {(item.tel ||
                            item.telephone ||
                            item.mail ||
                            item.email) && (
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                fontSize: "12px",
                                color: "#64748b",
                              }}
                            >
                              {item.tel || item.telephone ? (
                                <span>
                                  Téléphone: {item.tel || item.telephone}
                                </span>
                              ) : null}
                              {item.mail || item.email ? (
                                <span>Email: {item.mail || item.email}</span>
                              ) : null}
                            </div>
                          )} */}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {showOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-lg">
            <div className="grid grid-cols-3 gap-5">
              {/* Titre */}
              <div className="col-span-3 text-center">
                <h2 className="text-lg font-semibold text-slate-700">
                  Veuillez sélectionner quel dossier voulez-vous créer
                </h2>
              </div>

              {/* Boutons */}
              <div className="col-span-3 grid grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    setShowOverlay(false);
                    setShowAddClientOverlay(true);
                  }}
                  className="w-full py-4 rounded-xl border border-slate-300 text-slate-700 font-semibold
                       hover:bg-slate-100 transition"
                >
                  Client
                </button>

                <button
                  onClick={() => {
                    setShowOverlay(false);
                    setShowFromLeadOverlay(true);
                  }}
                  className="w-full py-4 rounded-xl border border-slate-300 text-slate-700 font-semibold
                       hover:bg-slate-100 transition"
                >
                  Prospect
                </button>
              </div>

              {/* Warning */}
              <div className="col-span-3 mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                ⚠️ Une fois le dossier créé, veuillez actualiser la plateforme
                avant de procéder à la recherche de ce dossier.
              </div>
            </div>

            {/* Annuler */}
            <div className="mt-5 text-center">
              <button
                onClick={() => setShowOverlay(false)}
                className="text-sm text-slate-500 hover:text-slate-700 hover:underline"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
      {showAddClientOverlay && (
        <div className="overlayAC" ref={overlayRef}>
          <div className="inoverlayLead" ref={inoverlayRef}>
            <button
              className="close-button"
              onClick={() => setShowAddClientOverlay(false)}
              style={{
                position: "absolute",
                top: "50px",
                right: "425px",
                background: "transparent",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
              }}
            >
              &times;
            </button>
            <AddClient onCancel={() => setShowAddClientOverlay(false)} />
          </div>
        </div>
      )}
      {showFromLeadOverlay && (
        <div className="overlayAC" ref={overlayRef}>
          <div className="inoverlayLead" ref={inoverlayRef}>
            <button
              className="close-button"
              onClick={() => setShowFromLeadOverlay(false)}
              style={{
                position: "absolute",
                top: "35px",
                right: "425px",
                background: "transparent",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
              }}
            >
              &times;
            </button>
            <FormLead onCancel={() => setShowFromLeadOverlay(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
